import { useEffect, useRef, useState } from 'react';

export interface CropPct {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface BannerCropEditorProps {
  mediaUrl: string;
  mediaKind: 'image' | 'video';
  crop: CropPct;
  onChange: (crop: CropPct) => void;
  /** Maximum total trim per axis (top+bottom and left+right). Default 95% so
   *  there's always at least a 5% strip to keep. */
  maxTotalPct?: number;
}

type DragMode =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'move'
  | null;

const HANDLE = 12; // px

/**
 * Visual banner cropping. The crop is expressed as 4 per-side trim
 * percentages — same model the backend uses for ffmpeg. Drag any edge or
 * corner to resize the kept region; drag inside it to move the whole window.
 */
export function BannerCropEditor({
  mediaUrl,
  mediaKind,
  crop,
  onChange,
  maxTotalPct = 95,
}: BannerCropEditorProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // The editor box must take the media's true aspect ratio so the media fills
  // it with no letterbox — otherwise crop percentages (measured against the
  // box) wouldn't match the media, and ffmpeg (which crops by % of the real
  // banner) would distort the result (e.g. a tall banner rendered square).
  const [mediaAspect, setMediaAspect] = useState<number | null>(null);
  const [drag, setDrag] = useState<{
    mode: DragMode;
    startX: number;
    startY: number;
    startCrop: CropPct;
    rect: DOMRect;
  } | null>(null);

  useEffect(() => {
    if (!drag) return;
    function onMove(e: PointerEvent): void {
      if (!drag) return;
      const dxPct = ((e.clientX - drag.startX) / drag.rect.width) * 100;
      const dyPct = ((e.clientY - drag.startY) / drag.rect.height) * 100;
      const c = { ...drag.startCrop };

      const clampSide = (v: number, opposite: number): number =>
        Math.max(0, Math.min(maxTotalPct - opposite, v));

      switch (drag.mode) {
        case 'top':
          c.top = clampSide(drag.startCrop.top + dyPct, drag.startCrop.bottom);
          break;
        case 'bottom':
          c.bottom = clampSide(drag.startCrop.bottom - dyPct, drag.startCrop.top);
          break;
        case 'left':
          c.left = clampSide(drag.startCrop.left + dxPct, drag.startCrop.right);
          break;
        case 'right':
          c.right = clampSide(drag.startCrop.right - dxPct, drag.startCrop.left);
          break;
        case 'top-left':
          c.top = clampSide(drag.startCrop.top + dyPct, drag.startCrop.bottom);
          c.left = clampSide(drag.startCrop.left + dxPct, drag.startCrop.right);
          break;
        case 'top-right':
          c.top = clampSide(drag.startCrop.top + dyPct, drag.startCrop.bottom);
          c.right = clampSide(drag.startCrop.right - dxPct, drag.startCrop.left);
          break;
        case 'bottom-left':
          c.bottom = clampSide(drag.startCrop.bottom - dyPct, drag.startCrop.top);
          c.left = clampSide(drag.startCrop.left + dxPct, drag.startCrop.right);
          break;
        case 'bottom-right':
          c.bottom = clampSide(drag.startCrop.bottom - dyPct, drag.startCrop.top);
          c.right = clampSide(drag.startCrop.right - dxPct, drag.startCrop.left);
          break;
        case 'move': {
          // Preserve crop window size; shift top/bottom and left/right together.
          const hKept = 100 - drag.startCrop.top - drag.startCrop.bottom;
          const wKept = 100 - drag.startCrop.left - drag.startCrop.right;
          const maxTop = 100 - hKept;
          const maxLeft = 100 - wKept;
          c.top = Math.max(0, Math.min(maxTop, drag.startCrop.top + dyPct));
          c.bottom = 100 - c.top - hKept;
          c.left = Math.max(0, Math.min(maxLeft, drag.startCrop.left + dxPct));
          c.right = 100 - c.left - wKept;
          break;
        }
        case null:
          return;
      }
      onChange({
        top: round(c.top),
        right: round(c.right),
        bottom: round(c.bottom),
        left: round(c.left),
      });
    }
    function onUp(): void {
      setDrag(null);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [drag, onChange, maxTotalPct]);

  function startDrag(mode: DragMode, e: React.PointerEvent<HTMLDivElement>): void {
    e.stopPropagation();
    e.preventDefault();
    const el = containerRef.current;
    if (!el) return;
    setDrag({
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startCrop: { ...crop },
      rect: el.getBoundingClientRect(),
    });
  }

  // Crop rectangle position/size as inline CSS percentages.
  const cropStyle: React.CSSProperties = {
    position: 'absolute',
    top: `${crop.top}%`,
    left: `${crop.left}%`,
    right: `${crop.right}%`,
    bottom: `${crop.bottom}%`,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
    border: '2px solid var(--neon)',
    cursor: 'move',
    boxSizing: 'border-box',
  };

  const handleBase: React.CSSProperties = {
    position: 'absolute',
    width: HANDLE,
    height: HANDLE,
    background: 'var(--neon)',
    border: '2px solid #fff',
    borderRadius: '50%',
    zIndex: 2,
  };

  const edgeBase: React.CSSProperties = {
    position: 'absolute',
    background: 'transparent',
    zIndex: 1,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 360,
          maxHeight: '65vh',
          margin: '0 auto',
          background: '#000',
          // Match the media's real aspect so it fills the box edge-to-edge with
          // no letterbox → crop % map 1:1 to the banner. Fallback 9:16 until load.
          aspectRatio: mediaAspect ? String(mediaAspect) : '9 / 16',
          overflow: 'hidden',
          userSelect: 'none',
          touchAction: 'none',
        }}
      >
        {mediaKind === 'image' ? (
          <img
            crossOrigin="anonymous"
            src={mediaUrl}
            alt=""
            onLoad={(e) => {
              const el = e.currentTarget;
              if (el.naturalWidth && el.naturalHeight) setMediaAspect(el.naturalWidth / el.naturalHeight);
            }}
            style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
            draggable={false}
          />
        ) : (
          <video
            crossOrigin="anonymous"
            src={mediaUrl}
            preload="metadata"
            muted
            playsInline
            onLoadedMetadata={(e) => {
              const el = e.currentTarget;
              if (el.videoWidth && el.videoHeight) setMediaAspect(el.videoWidth / el.videoHeight);
            }}
            style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
          />
        )}

        {/* Crop window (mask outside via boxShadow) + move handle */}
        <div style={cropStyle} onPointerDown={(e) => startDrag('move', e)}>
          {/* Edge zones — thin clickable strips for edge resize */}
          <div
            style={{ ...edgeBase, top: -4, left: 0, right: 0, height: 8, cursor: 'ns-resize' }}
            onPointerDown={(e) => startDrag('top', e)}
          />
          <div
            style={{ ...edgeBase, bottom: -4, left: 0, right: 0, height: 8, cursor: 'ns-resize' }}
            onPointerDown={(e) => startDrag('bottom', e)}
          />
          <div
            style={{ ...edgeBase, top: 0, bottom: 0, left: -4, width: 8, cursor: 'ew-resize' }}
            onPointerDown={(e) => startDrag('left', e)}
          />
          <div
            style={{ ...edgeBase, top: 0, bottom: 0, right: -4, width: 8, cursor: 'ew-resize' }}
            onPointerDown={(e) => startDrag('right', e)}
          />

          {/* Corner handles */}
          <div
            style={{ ...handleBase, top: -HANDLE / 2, left: -HANDLE / 2, cursor: 'nwse-resize' }}
            onPointerDown={(e) => startDrag('top-left', e)}
          />
          <div
            style={{ ...handleBase, top: -HANDLE / 2, right: -HANDLE / 2, cursor: 'nesw-resize' }}
            onPointerDown={(e) => startDrag('top-right', e)}
          />
          <div
            style={{ ...handleBase, bottom: -HANDLE / 2, left: -HANDLE / 2, cursor: 'nesw-resize' }}
            onPointerDown={(e) => startDrag('bottom-left', e)}
          />
          <div
            style={{ ...handleBase, bottom: -HANDLE / 2, right: -HANDLE / 2, cursor: 'nwse-resize' }}
            onPointerDown={(e) => startDrag('bottom-right', e)}
          />

          {/* Percent labels on each side */}
          <SideLabel side="top" value={crop.top} />
          <SideLabel side="right" value={crop.right} />
          <SideLabel side="bottom" value={crop.bottom} />
          <SideLabel side="left" value={crop.left} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', fontSize: 11, color: 'var(--text-3)' }}>
        <span>↑ {crop.top.toFixed(0)}%</span>
        <span>→ {crop.right.toFixed(0)}%</span>
        <span>↓ {crop.bottom.toFixed(0)}%</span>
        <span>← {crop.left.toFixed(0)}%</span>
        <button
          type="button"
          onClick={() => onChange({ top: 0, right: 0, bottom: 0, left: 0 })}
          style={{ fontSize: 11, padding: '2px 8px' }}
        >
          Reset
        </button>
      </div>
    </div>
  );
}

function SideLabel({ side, value }: { side: 'top' | 'right' | 'bottom' | 'left'; value: number }): React.ReactElement | null {
  if (value < 1) return null;
  const base: React.CSSProperties = {
    position: 'absolute',
    background: 'rgba(0, 0, 0, 0.65)',
    color: '#fff',
    fontSize: 10,
    padding: '1px 5px',
    borderRadius: 3,
    pointerEvents: 'none',
    fontFamily: 'var(--mono, monospace)',
  };
  switch (side) {
    case 'top':
      return <div style={{ ...base, top: 2, left: '50%', transform: 'translateX(-50%)' }}>↑ {value.toFixed(0)}%</div>;
    case 'right':
      return <div style={{ ...base, right: 2, top: '50%', transform: 'translateY(-50%)' }}>→ {value.toFixed(0)}%</div>;
    case 'bottom':
      return <div style={{ ...base, bottom: 2, left: '50%', transform: 'translateX(-50%)' }}>↓ {value.toFixed(0)}%</div>;
    case 'left':
      return <div style={{ ...base, left: 2, top: '50%', transform: 'translateY(-50%)' }}>← {value.toFixed(0)}%</div>;
  }
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}
